import { Button } from '../../../components/ui/Button/Button';
import type { BulkSendOutcome, BulkSendResult, BulkSendSkipReason } from '../../../types/api';

const SKIP_TEXT: Record<BulkSendSkipReason, string> = {
  not_approved: 'not approved yet',
  already_sent: 'already sent',
  no_contact: 'no email or phone',
  no_email: 'no email address',
  no_phone: 'no phone number',
  duplicate_in_request: 'selected twice',
  time_budget: 'not reached before the time limit; send again',
};

function describe(o: BulkSendOutcome): string {
  if (o.status === 'failed') return o.error ?? 'failed';
  if (o.status === 'skipped') return o.reason ? SKIP_TEXT[o.reason] : 'skipped';
  return `queued by ${o.via ?? 'email'}`;
}

interface BulkSendSummaryProps {
  result: BulkSendResult;
  onDismiss: () => void;
}

/** Result of a bulk send: counts, then every receipt that was NOT queued with its reason. */
export function BulkSendSummary({ result, onDismiss }: BulkSendSummaryProps): JSX.Element {
  const problems = result.results.filter((r) => r.status !== 'queued');
  return (
    <div
      role="status"
      data-testid="bulk-send-summary"
      style={{
        margin: '0.75rem 1rem',
        padding: '0.75rem 1rem',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        background: 'var(--color-surface)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <strong>
          {result.queued} queued · {result.skipped} skipped · {result.failed} failed
        </strong>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      {result.remaining_unsent !== undefined && result.remaining_unsent > 0 ? (
        <p style={{ margin: '0.5rem 0 0' }}>
          {result.remaining_unsent} unsent receipt{result.remaining_unsent === 1 ? '' : 's'} still
          waiting. Run Send all unsent again to continue.
        </p>
      ) : null}
      {problems.length > 0 ? (
        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
          {problems.map((o) => (
            <li key={o.receipt_id}>
              {o.reference ?? o.receipt_id}: {o.status === 'failed' ? 'failed, ' : ''}
              {describe(o)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
